import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { authFetch } from "../utils/authFetch";
import toastr from "toastr";
import { Helmet } from "react-helmet";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/* ---------- Toastr ---------- */
const toast = (m, t = "success") => {
  toastr.options = { closeButton: true, progressBar: true, timeOut: 3000 };
  toastr[t](m);
};

/* ---------- Export Helpers ---------- */
// const exportToExcel = (data, fileName = "AccountStatements.xlsx") => {
//   const worksheet = XLSX.utils.json_to_sheet(data);
//   const workbook = XLSX.utils.book_new();
//   XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");
//   XLSX.writeFile(workbook, fileName);
// };

// Function to flatten the report data
const flattenReportData = (data) => {
  return data.map((row, i) => {
    const ref = row.reference_details || "";

    // Extract fields separately
    const txnType = ref.match(/Transaction Type:\s*(.*)/)?.[1] || "-";
    const fromWallet = ref.match(/From:\s*(.*)/)?.[1] || "-";
    const toWallet = ref.match(/To:\s*(.*)/)?.[1] || "-";
    const txnId = ref.match(/TXN ID:\s*(.*)/)?.[1] || "-";

    const amount = parseFloat(row.amount_details?.match(/Amount:\s*₹([\d.]+)/)?.[1] || "0");
    const charge = parseFloat(row.amount_details?.match(/Charge:\s*₹([\d.]+)/)?.[1] || "0");
    const gst = parseFloat(row.amount_details?.match(/GST:\s*₹([\d.]+)/)?.[1] || "0");
    const total = parseFloat(row.amount_details?.match(/Total:\s*₹([\d.]+)/)?.[1] || "0");

    return {
      "#": i + 1,
      Date: row.date || "-",

      "User Details": row.user_details?.replace(/\n/g, " ") || "-",

      // Separate reference details
      "Transaction Type": txnType,
      "From Wallet": fromWallet,
      "To Wallet": toWallet,
      "TXN ID": txnId,

      // Wallet Type (credit/debit)
      "TXN Type": row.trans_type || "-",

      Amount: Number(amount.toFixed(2)),
      Charge: Number(charge.toFixed(2)),
      GST: Number(gst.toFixed(2)),
      Total: Number(total.toFixed(2)),
      "Opening Balance": row.opening_balance ?? "-",
      // "Closing Balance": row.closing_balance ?? "-",
      Status: row.status || "-"
    };
  });
};






// Function to export data to Excel
const exportToExcel = (data, fileName = "PayinSattlementStatement.xlsx") => {
  const formattedData = flattenReportData(data);

  if (!formattedData.length) {
    console.error("No data to export!");
    return;
  }

  const worksheet = XLSX.utils.json_to_sheet(formattedData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");

  XLSX.writeFile(workbook, fileName);
};




const exportToPDF = (data, fileName = "PayinAccountStatements.pdf") => {
  const doc = new jsPDF();
  doc.text("Account Statement Reports", 14, 10);

  if (data.length > 0) {
    const tableColumn = [
      "#",
      "Date",
      "User",
      "Reference",
      "Opening Bal",
      "Amount",
      "Type",
      "Closing Bal",
      "Remark",
      "Status",
    ];
    const tableRows = data.map((row, i) => [
      i + 1,
      row.date || "-",
      row.user_details || "-",
      row.reference_details || "-",
      row.opening_balance || "-",
      row.amount_details || "-",
      row.trans_type || "-",
      row.closing_balance || "-",
      // row.remark || "-",
      row.status || "-",
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: "striped",
      headStyles: { fillColor: [22, 160, 133] },
      styles: { fontSize: 10 },
    });
  }

  doc.save(fileName);
};


export default function ReportList() {
  const today = dayjs().format("YYYY-MM-DD");

  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [searchText, setSearchText] = useState("");
  const [filteredReports, setFilteredReports] = useState([]);

  const loadReports = async () => {
    let url = "/api/v1/wallet/payintransfertopayout";
    const params = new URLSearchParams();
    params.append("start", 0);
    params.append("length", 500);
    params.append("order_column", "created_at");
    params.append("order_dir", "desc");
    if (statusFilter) params.append("status", statusFilter);
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);

    try {
      const r = await authFetch(`${url}?${params.toString()}`);
      const d = await r.json();
      // setReports(Array.isArray(d.data) ? d.data : []);
      setReports(Array.isArray(d) ? d : []);
    } catch (err) {
      console.error(err);
      toast("Failed to load reports", "error");
    }
  };

  useEffect(() => {
    loadReports();
  }, [statusFilter, fromDate, toDate]);

  useEffect(() => {
    const filtered = reports.filter((report) =>
      Object.values(report)
        .join(" ")
        .toLowerCase()
        .includes(searchText.toLowerCase())
    );
    setFilteredReports(filtered);
  }, [searchText, reports]);

  /* ---------- Table Columns ---------- */
  const columns = [
  {
    id: "date",
    name: "# / Date",
    cell: (row, i) => (
      <div>
        <div className="fw-bold">{i + 1}</div>
        <div className="text-danger fw-bold">{row.date}</div>
      </div>
    ),
    sortable: true,
  },
  { name: "User Details", selector: (row) => row.user_details, wrap: true },
  { name: "Reference", selector: (row) => row.reference_details, wrap: true },
  { name: "Opening Bal", selector: (row) => parseFloat((row.opening_balance ?? 0).toFixed(2))},
  {
    name: "Amount",
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.amount_details}
      </div>
    ),
    sortable: true
  },
  { name: "Type", selector: (row) => row.trans_type },
//   { name: "Closing Bal", selector: (row) => parseFloat((row.closing_balance ?? 0).toFixed(2))},
  { name: "Remark", selector: (row) => row.remark, wrap: true },
  {
    name: "Status",
    cell: (row) => (
      <span
        className={`btn btn-sm btn-block radius-30 ${
          (row.status || "").toLowerCase() === "success"
            ? "btn-light-success text-black"
            : (row.status || "").toLowerCase() === "failed"
            ? "btn-light-danger"
            : (row.status || "").toLowerCase() === "pending"
            ? "btn-light-warning"
            : "btn-light-primary"
        }`}
      >
        {row.status}
      </span>
    ),
    sortable: true,
  },
];

  return (
    <>
      <Helmet>
        <title>Payin Transfer To Payout | SuvidhaPe</title>
      </Helmet>

      <div className="page-wrapper">
        <div className="page-content-wrapper">
          <div className="page-content">
            <div className="card">
              <div className="card-body">
                <h4 className="mb-3">Payin Transfer To Payout Reports</h4>

                {/* Filters + Export */}
                <div className="mb-3 d-flex gap-2 flex-wrap">
                  <input
                    type="date"
                    className="form-control w-auto"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                  <input
                    type="date"
                    className="form-control w-auto"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                  {/* <select
                    className="form-select w-auto"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="success">Success</option>
                    <option value="failed">Failed</option>
                  </select> */}
                  <input
                    type="text"
                    className="form-control w-auto"
                    placeholder="Type to search..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={loadReports}
                  >
                    Apply Filters
                  </button>
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => exportToExcel(reports)}
                  >
                    Download Excel
                  </button>
                  {/*<button
                    className="btn btn-danger btn-sm"
                    onClick={() => exportToPDF(reports)}
                  >
                    Download PDF
                  </button> */}
                </div>

                <DataTable
                  columns={columns}
                  // data={reports}
                  data={filteredReports}
                  pagination
                  highlightOnHover
                  striped
                  defaultSortFieldId="date"
                  defaultSortAsc={false}
                  persistTableHead
                  paginationRowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
